<script setup>
/* global axios */
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import {
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
  KANBAN_MODULES_MATRIX,
  MODULE_CRUD_ACTIONS,
  modulePermission,
  CALL_PERMISSIONS,
  CALL_PERMISSION_MODULE_BY_KEY,
  MANAGE_ALL_CONVERSATION_PERMISSIONS,
  CONVERSATION_UNASSIGNED_PERMISSIONS,
  CONVERSATION_PARTICIPATING_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
import { useAccount } from 'dashboard/composables/useAccount';

import Button from 'dashboard/components-next/button/Button.vue';
import AccessPolicySection from './AccessPolicySection.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const { currentAccount } = useAccount();

// Cada permissão de Chamadas (Wavoip/AstraCall/BCVOZ) só aparece se o módulo
// dela estiver habilitado no Super Admin. Módulos OPT-IN (nascem desligados)
// usam `=== true`; os demais, `!== false`.
const MODULE_DEFAULT_OFF = ['bcvoz'];
const moduleOn = key =>
  MODULE_DEFAULT_OFF.includes(key)
    ? currentAccount.value?.feature_modules?.[key] === true
    : currentAccount.value?.feature_modules?.[key] !== false;
const visibleCallPermissions = computed(() =>
  CALL_PERMISSIONS.filter(p => moduleOn(CALL_PERMISSION_MODULE_BY_KEY[p]))
);

const name = ref('');
const description = ref('');
const selectedPermissions = ref([]);

// Restrição de acesso (horário + IP) — salva num endpoint próprio do fork após criar/editar.
const WDAYS = ['0', '1', '2', '3', '4', '5', '6'];
const defaultPolicy = () => ({
  enabled: false,
  timezone: 'America/Sao_Paulo',
  schedule: {
    enabled: false,
    days: Object.fromEntries(
      WDAYS.map(k => [
        k,
        {
          on: ['1', '2', '3', '4', '5'].includes(k),
          start: '08:00',
          end: '18:00',
          break: null,
        },
      ])
    ),
  },
  ip: { enabled: false, allow: [] },
});
const accessPolicy = ref(defaultPolicy());

// Abas do editor (o modal ficava grande demais numa coluna só).
const activeTab = ref('permissions');
const TABS = computed(() => [
  { key: 'permissions', label: t('CUSTOM_ROLE.TABS.PERMISSIONS') },
  { key: 'modules', label: t('CUSTOM_ROLE.TABS.MODULES') },
  { key: 'access', label: t('CUSTOM_ROLE.TABS.ACCESS') },
]);

const policyBase = () =>
  `/api/v1/accounts/${currentAccount.value.id}/kanban/role_access_policies`;

const applyFetchedPolicy = pol => {
  const base = defaultPolicy();
  if (pol && typeof pol === 'object') {
    base.enabled = !!pol.enabled;
    base.timezone = pol.timezone || base.timezone;
    if (pol.schedule) {
      base.schedule.enabled = !!pol.schedule.enabled;
      const days = pol.schedule.days || {};
      WDAYS.forEach(k => {
        if (days[k]) {
          base.schedule.days[k] = {
            on: !!days[k].on,
            start: days[k].start || '08:00',
            end: days[k].end || '18:00',
            break:
              days[k].break && days[k].break.start
                ? { start: days[k].break.start, end: days[k].break.end }
                : null,
          };
        }
      });
    }
    if (pol.ip) {
      base.ip.enabled = !!pol.ip.enabled;
      base.ip.allow = Array.isArray(pol.ip.allow) ? pol.ip.allow : [];
    }
  }
  accessPolicy.value = base;
};

const loadPolicy = async id => {
  try {
    const { data } = await axios.get(`${policyBase()}/${id}`);
    applyFetchedPolicy(data?.data?.access_policy);
  } catch (e) {
    accessPolicy.value = defaultPolicy();
  }
};

const savePolicy = async id => {
  if (!id) return;
  await axios.patch(`${policyBase()}/${id}`, {
    access_policy: accessPolicy.value,
  });
};

const nameInput = ref(null);

const addCustomRole = reactive({
  showLoading: false,
  message: '',
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  description: { required },
  selectedPermissions: { required, minLength: minLength(1) },
}));

const v$ = useVuelidate(rules, { name, description, selectedPermissions });

const resetForm = () => {
  name.value = '';
  description.value = '';
  selectedPermissions.value = [];
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedRole.name || '';
  description.value = props.selectedRole.description || '';
  selectedPermissions.value = props.selectedRole.permissions || [];
};

watch(
  selectedPermissions,
  (newValue, oldValue) => {
    // Check if manage all conversation permission is added or removed
    const hasAddedManageAllConversation =
      newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);
    const hasRemovedManageAllConversation =
      oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);

    if (hasAddedManageAllConversation) {
      // If manage all conversation permission is added,
      // then add unassigned and participating permissions automatically
      selectedPermissions.value = [
        ...new Set([
          ...selectedPermissions.value,
          CONVERSATION_UNASSIGNED_PERMISSIONS,
          CONVERSATION_PARTICIPATING_PERMISSIONS,
        ]),
      ];
    } else if (hasRemovedManageAllConversation) {
      // If manage all conversation permission is removed,
      // then only remove manage all conversation permission
      selectedPermissions.value = selectedPermissions.value.filter(
        p => p !== MANAGE_ALL_CONVERSATION_PERMISSIONS
      );
    }
  },
  { deep: true }
);

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
    loadPolicy(props.selectedRole.id);
  }
  // Focus the name input when mounted
  nameInput.value?.focus();
});

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `CUSTOM_ROLE.EDIT.${base}`
    : `CUSTOM_ROLE.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('SUBMIT')));

const handleCustomRole = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addCustomRole.showLoading = true;
  try {
    const roleData = {
      name: name.value,
      description: description.value,
      permissions: selectedPermissions.value,
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...roleData,
      });
      await savePolicy(props.selectedRole.id);
      useAlert(t('CUSTOM_ROLE.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      const created = await store.dispatch(
        'customRole/createCustomRole',
        roleData
      );
      await savePolicy(created?.id);
      useAlert(t('CUSTOM_ROLE.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`CUSTOM_ROLE.FORM.API.ERROR_MESSAGE`);
    useAlert(errorMessage);
  } finally {
    addCustomRole.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || addCustomRole.showLoading
);
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <form class="flex flex-col w-full" @submit.prevent="handleCustomRole">
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CUSTOM_ROLE.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model.trim="name"
            type="text"
            :placeholder="$t('CUSTOM_ROLE.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.description.$error }">
          {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL') }}

          <textarea
            v-model="description"
            :rows="3"
            :placeholder="$t('CUSTOM_ROLE.FORM.DESCRIPTION.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
        </label>
      </div>

      <!-- Abas do editor -->
      <div class="flex gap-1 mt-3 mb-3 border-b border-n-weak">
        <button
          v-for="tab in TABS"
          :key="tab.key"
          type="button"
          class="px-3 py-2 -mb-px text-sm border-b-2 border-transparent text-n-slate-11 hover:text-n-slate-12"
          :class="
            activeTab === tab.key
              ? '!border-n-brand text-n-slate-12 font-medium'
              : ''
          "
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
          <span
            v-if="tab.key === 'permissions' && v$.selectedPermissions.$error"
            class="ml-1 text-n-ruby-9"
            >•</span
          >
        </button>
      </div>

      <div v-show="activeTab === 'permissions'" class="w-full">
        <label :class="{ 'text-n-ruby-9': v$.selectedPermissions.$error }">
          {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL') }}
        </label>
        <div class="flex flex-col gap-2.5 mb-4 mt-2">
          <div
            v-for="permission in AVAILABLE_CUSTOM_ROLE_PERMISSIONS"
            :key="permission"
            class="flex items-center"
          >
            <input
              :id="permission"
              v-model="selectedPermissions"
              type="checkbox"
              :value="permission"
              name="permissions"
              class="ltr:mr-2 rtl:ml-2"
            />
            <label :for="permission" class="text-sm font-normal">
              {{ $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`) }}
            </label>
          </div>
        </div>
      </div>

      <div v-show="activeTab === 'modules'">
        <!-- Permissões de módulos extras (AstraChat) — matriz CRUD por módulo -->
        <div class="w-full">
          <label>{{ $t('CUSTOM_ROLE.FORM.KANBAN_MODULES.LABEL') }}</label>
        <div class="mb-4 mt-2 overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-n-slate-11">
                <th class="text-left font-normal pb-1.5" />
                <th class="font-normal pb-1.5 px-2 w-16 text-center">
                  {{ $t('CUSTOM_ROLE.CRUD.READ') }}
                </th>
                <th
                  v-for="action in MODULE_CRUD_ACTIONS"
                  :key="action"
                  class="font-normal pb-1.5 px-2 w-16 text-center"
                >
                  {{ $t(`CUSTOM_ROLE.CRUD.${action.toUpperCase()}`) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="mod in KANBAN_MODULES_MATRIX"
                :key="mod.key"
                class="border-t border-n-weak"
              >
                <td class="py-1.5 pr-2 font-normal">
                  {{ $t(`CUSTOM_ROLE.MODULES.${mod.key.toUpperCase()}`) }}
                </td>
                <td class="text-center px-2">
                  <input
                    :id="modulePermission(mod.key, 'read')"
                    v-model="selectedPermissions"
                    type="checkbox"
                    :value="modulePermission(mod.key, 'read')"
                    name="permissions"
                  />
                </td>
                <td
                  v-for="action in MODULE_CRUD_ACTIONS"
                  :key="action"
                  class="text-center px-2"
                >
                  <input
                    v-if="mod.crud"
                    v-model="selectedPermissions"
                    type="checkbox"
                    :value="modulePermission(mod.key, action)"
                    name="permissions"
                  />
                  <span v-else class="text-n-slate-8">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Chamadas — cada permissão só aparece se o módulo dela estiver habilitado -->
      <div v-if="visibleCallPermissions.length" class="w-full">
        <label>{{ $t('CUSTOM_ROLE.FORM.CALLS.LABEL') }}</label>
        <div class="flex flex-col gap-2.5 mb-4 mt-2">
          <div
            v-for="permission in visibleCallPermissions"
            :key="permission"
            class="flex items-center"
          >
            <input
              :id="permission"
              v-model="selectedPermissions"
              type="checkbox"
              :value="permission"
              name="permissions"
              class="ltr:mr-2 rtl:ml-2"
            />
            <label :for="permission" class="text-sm font-normal">
              {{ $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`) }}
            </label>
          </div>
        </div>
      </div>

      </div>

      <div v-show="activeTab === 'access'">
        <AccessPolicySection v-model="accessPolicy" />
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="addCustomRole.showLoading"
        />
      </div>
    </form>
  </div>
</template>
